/**
 * /prompts — list page (Story 7.6 AC2.a).
 *
 * Viewer+ can reach this page; only operator+ sees the "Novo prompt" action.
 * Role gating is enforced server-side — the UI simply hides the button for
 * viewers so the UX is not confusing.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import type { Prompt, PromptListResponse } from '@/lib/prompts-types';

const PROMPTS_QUERY_KEY = ['admin', 'prompts'] as const;

function canMutate(role: string | undefined): boolean {
  return role === 'operator' || role === 'admin';
}

export function PromptsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [creatorOpen, setCreatorOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: PROMPTS_QUERY_KEY,
    queryFn: () => apiClient.get<PromptListResponse>('/admin/prompts'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Prompts</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie prompts de IA com versionamento e rollback auditável.
          </p>
        </div>
        {canMutate(user?.role) && (
          <Button onClick={() => setCreatorOpen(true)}>
            <Plus /> Novo prompt
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Prompts cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {isError && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">Falha ao carregar prompts.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}
          {data && data.data.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum prompt cadastrado ainda.</p>
          )}
          {data && data.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Nome</th>
                    <th className="py-2 pr-4 font-medium">Descrição</th>
                    <th className="py-2 pr-4 font-medium">Versão ativa</th>
                    <th className="py-2 pr-4 font-medium">Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((p) => (
                    <tr key={p.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/prompts/${p.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {p.name}
                        </Link>
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">{p.description ?? '—'}</td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {p.activeVersionId ? (
                          <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                            ativa
                          </span>
                        ) : (
                          <span className="text-xs italic">sem versão</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {formatDateTime(p.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {canMutate(user?.role) && (
        <CreatePromptDialog
          open={creatorOpen}
          onOpenChange={setCreatorOpen}
          onCreated={(created) => {
            // Optimistic-ish update: prepend the new row so the UX feels instant
            // while the invalidation round-trip is in flight.
            qc.setQueryData<PromptListResponse | undefined>(PROMPTS_QUERY_KEY, (prev) =>
              prev ? { data: [created, ...prev.data] } : { data: [created] },
            );
            void qc.invalidateQueries({ queryKey: PROMPTS_QUERY_KEY });
          }}
        />
      )}
    </div>
  );
}

interface CreatePromptDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: (prompt: Prompt) => void;
}

function CreatePromptDialog({ open, onOpenChange, onCreated }: CreatePromptDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      apiClient.post<Prompt>('/admin/prompts', body),
    onSuccess: (created) => {
      toast.success(`Prompt "${created.name}" criado.`);
      onCreated(created);
      setName('');
      setDescription('');
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Nome já utilizado por outro prompt.');
        return;
      }
      toast.error('Não foi possível criar o prompt.');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo prompt</DialogTitle>
          <DialogDescription>
            Crie um container lógico. Você adicionará versões na tela de detalhe.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (!trimmed) {
              toast.error('Informe um nome para o prompt.');
              return;
            }
            mutation.mutate({
              name: trimmed,
              description: description.trim() || undefined,
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="prompt-name">Nome *</Label>
            <Input
              id="prompt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="upsell_educacao_v1"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prompt-description">Descrição</Label>
            <textarea
              id="prompt-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Curto resumo do uso do prompt (opcional)."
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Criando…' : 'Criar prompt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PromptsPage;
